import React, { Fragment, type LegacyRef, type MutableRefObject, type ReactElement } from 'react';

import 'react-datepicker/dist/react-datepicker.css';

import classnames from 'classnames';
import { endOfDay, startOfDay } from 'date-fns';
import compareDesc from 'date-fns/fp/compareDesc';
import ReactDatePicker, { type ReactDatePickerProps } from 'react-datepicker';

import useTranslation from '../../features/locale/useTranslation';
import useOutsideClick from '../../hooks/useOutsideClick';
import { locales, transformLanguageCode } from '../../utils';
import { Divider } from '../Divider';

import { CalendarSidePanelItem } from './components/CalendarSidePanelItem';
import { CountdownLabel } from './components/CountdownLabel';
import usePanelDatesPreview from './hooks/usePanelDatesPreview';

import styles from './Calendar.module.scss';

export type CalendarDateValue = Date | null;
export type CalendarDateInputValue = Date | null;
export type CalendarChangeValue = CalendarDateValue | [CalendarDateValue, CalendarDateValue];

export type ReactDatePickerType = ReactDatePicker;

type CalendarBasePanelItemType = {
    title: string;
    divider?: boolean;
    metadata?: Record<string, unknown>;
};

export type CalendarSinglePanelItemType = {
    value: Date | null;
    /**
     * Callback when the item is selected.
     */
    onSelect?: (
        item: CalendarSinglePanelItemType,
        event: React.MouseEvent<HTMLButtonElement>
    ) => void;
} & CalendarBasePanelItemType;

export type CalendarRangePanelItemType = {
    start: Date | null;
    end: Date | null;
    /**
     * Callback when the item is selected.
     */
    onSelect?: (
        item: CalendarRangePanelItemType,
        event: React.MouseEvent<HTMLButtonElement>
    ) => void;
} & CalendarBasePanelItemType;

export type CalendarPanelItemType = CalendarSinglePanelItemType | CalendarRangePanelItemType;
export type CalendarChangeEvent = {
    panelItem: CalendarPanelItemType | null;
};
export const DEFAULT_CHANGE_EVENT: CalendarChangeEvent = Object.freeze({
    panelItem: null
});

const parseOnChangeValue = (value: CalendarChangeValue): CalendarChangeValue => {
    if (Array.isArray(value)) {
        const start = value[0] ? startOfDay(value[0]) : null;
        const end = value[1] ? endOfDay(value[1]) : null;
        return [start, end];
    }
    return value;
};

export type CalendarProps = {
    className?: string;
    /**
     * If `true`, the user will not be able to interact with the input.
     */
    disabled?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    minDate?: CalendarDateInputValue;
    maxDate?: CalendarDateInputValue;
    panelItems?: CalendarPanelItemType[];
    selectsRange?: boolean;
    showCountdownLabel?: boolean;
    countdownBaseDate?: Date | null;
    excludeDates?: Date[] | undefined;
    customInput?: ReactElement;
    innerRef?: LegacyRef<ReactDatePicker>;
    id?: string;
    monthsShown?: number;
    selected?: CalendarDateInputValue;
    startDate?: CalendarDateInputValue;
    endDate?: CalendarDateInputValue;
    onChange: (value: CalendarChangeValue, event: CalendarChangeEvent) => void;
    open?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
    inline?: boolean;
    dateFormat?: string;
    shouldCloseOnSelect?: boolean;
    reactDatePickerProps?: Omit<ReactDatePickerProps, keyof CalendarProps>;
};

export const Calendar = ({
    className,
    placeholder,
    showCountdownLabel = false,
    countdownBaseDate = null,
    minDate,
    maxDate,
    panelItems = [],
    disabled = false,
    readOnly = false,
    selectsRange,
    excludeDates,
    innerRef,
    startDate: startDateProp,
    endDate: endDateProp,
    selected,
    onChange,
    onClose,
    onOpen,
    shouldCloseOnSelect = true,
    ...rest
}: CalendarProps): ReactElement => {
    const { currentLanguage } = useTranslation();
    const hasPanelItems = panelItems?.length > 0;
    const selectedStartDate = startDateProp ?? selected ?? null;
    const selectedEndDate = endDateProp ?? null;

    const {
        startDatePreview: currentStartDate,
        endDatePreview: currentEndDate,
        startDatePreviewLabel,
        endDatePreviewLabel,
        updatePreview
    } = usePanelDatesPreview({
        startDate: selectedStartDate,
        endDate: selectedEndDate
    });
    const isPreviewIncomplete = !currentEndDate;
    const computedCountdownBaseDate = countdownBaseDate ?? new Date();
    const isSelectedInFuture =
        compareDesc(computedCountdownBaseDate, selectedStartDate ?? computedCountdownBaseDate) ===
        -1;

    // we must ensure the maxDate and minDate falsy values are `undefined` instead of `null`
    // otherwise the keyboard navigation will not work.
    const computedMaxDate = maxDate || undefined;
    const computedMinDate = minDate || undefined;

    const handleUserSelection = (value: CalendarChangeValue, event: CalendarChangeEvent) => {
        updatePreview(value);
        const isComplete = selectsRange
            ? Array.isArray(value) && value.every(Boolean)
            : value !== undefined;
        if (isComplete) {
            onChange?.(parseOnChangeValue(value), event);
            if (shouldCloseOnSelect) {
                onClose?.();
            }
        }
    };

    const parsePanelItemValue = (
        item: CalendarSinglePanelItemType | CalendarRangePanelItemType
    ): CalendarChangeValue => {
        if (selectsRange) {
            if ('start' in item) {
                return [item.start, item.end];
            }
        }
        if ('value' in item) {
            return item.value;
        }
        if ('start' in item) {
            // not a range, but received `start` instead of `value`, so we return `start` as single value
            return item.start;
        }
        return null;
    };

    const handlePanelItemSelect =
        (item: CalendarPanelItemType) => (event: React.MouseEvent<HTMLButtonElement>) => {
            handleUserSelection(parsePanelItemValue(item), {
                ...DEFAULT_CHANGE_EVENT,
                panelItem: item
            });
            item?.onSelect?.(item as never, event);
            if (item?.onSelect && shouldCloseOnSelect) {
                onClose?.();
            }
        };

    const handlePanelItemActive = (item: CalendarPanelItemType) => {
        updatePreview(parsePanelItemValue(item));
    };

    const handlePanelItemDeactivation = () => {
        updatePreview(selectedStartDate, selectedEndDate);
    };

    const handleDateChange = (value: CalendarChangeValue) => {
        handleUserSelection(value, DEFAULT_CHANGE_EVENT);
    };

    const handleClose = () => {
        onClose?.();
        if (selectsRange && isPreviewIncomplete) {
            updatePreview(selectedStartDate, selectedEndDate);
        }
    };

    const handleOutsideClick = () => {
        handleClose();
    };

    const [singleDateRangeRef]: unknown[] = useOutsideClick(handleOutsideClick, true, {
        capture: true
    });

    return (
        <div
            ref={singleDateRangeRef as MutableRefObject<HTMLDivElement>}
            className={classnames(className, styles.calendar, {
                [styles.selectsRange]: selectsRange
            })}
        >
            <ReactDatePicker
                calendarClassName={styles.datePicker}
                locale={locales[transformLanguageCode(currentLanguage)]}
                onChange={handleDateChange}
                selected={currentStartDate}
                startDate={currentStartDate}
                endDate={currentEndDate}
                minDate={computedMinDate}
                maxDate={computedMaxDate}
                selectsRange={selectsRange}
                excludeDates={excludeDates}
                disabled={disabled}
                readOnly={readOnly}
                placeholderText={placeholder}
                ref={innerRef}
                onCalendarClose={handleClose}
                onCalendarOpen={onOpen}
                shouldCloseOnSelect={shouldCloseOnSelect}
                fixedHeight={selectsRange}
                {...rest}
            >
                {hasPanelItems && (
                    <div className={styles.sidePanel}>
                        {panelItems.map((item) => (
                            <Fragment key={item.title}>
                                {item.divider && <Divider className={styles.divider} />}
                                <CalendarSidePanelItem
                                    key={item.title}
                                    item={item}
                                    onActive={handlePanelItemActive}
                                    onDeactivate={handlePanelItemDeactivation}
                                    onClick={handlePanelItemSelect(item)}
                                />
                            </Fragment>
                        ))}
                    </div>
                )}
                {selectsRange && (
                    <div
                        className={classnames(styles.selectedDates, {
                            [styles.hasPanelItems]: hasPanelItems
                        })}
                    >
                        {startDatePreviewLabel} - {endDatePreviewLabel}
                    </div>
                )}
            </ReactDatePicker>
            {showCountdownLabel && isSelectedInFuture && selectedStartDate && (
                <CountdownLabel startDate={selectedStartDate} currentLanguage={currentLanguage} />
            )}
        </div>
    );
};
